import React, { useEffect, useState } from 'react'
import { parsePhoneNumber } from 'react-phone-number-input'

import { Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'

// Horizontal (State B) is strictly wider than this ratio. A ratio exactly
// equal to the threshold is treated as non-horizontal (State C) per spec.
export const HORIZONTAL_RATIO_THRESHOLD = 2.5

export const LAYOUT_STATE = {
  TEXT: 'TEXT', // State A: text name + address
  HORIZONTAL: 'HORIZONTAL', // State B: wide logo centered, address below
  COMPACT: 'COMPACT', // State C: square/vertical logo left, address right
}

export const formattedLocationAddress = location => {
  return `${location.street}, ${location.city}`
}

export const formattedLocationPhone = location => {
  return parsePhoneNumber(location.phoneNumber, 'US').formatNational()
}

export const resolveLayoutState = (naturalWidth, naturalHeight) => {
  if (!naturalWidth || !naturalHeight) {
    return LAYOUT_STATE.TEXT
  }

  return naturalWidth / naturalHeight > HORIZONTAL_RATIO_THRESHOLD
    ? LAYOUT_STATE.HORIZONTAL
    : LAYOUT_STATE.COMPACT
}

const AddressBlock = ({ align, location }) => (
  <>
    <Typography align={align} sx={{ fontWeight: 500 }} variant="body1">
      {formattedLocationAddress(location)}
    </Typography>
    <Typography align={align} sx={{ fontWeight: 500 }} variant="body1">
      {formattedLocationPhone(location)}
    </Typography>
  </>
)

export const LocationBranding = ({ location }) => {
  const logoUrl = location?.bookingLogoUrl
  const [layoutState, setLayoutState] = useState(LAYOUT_STATE.TEXT)
  // When there is no logo URL, State A is known immediately. Otherwise we wait
  // for the off-screen image to load before committing a visible state, so the
  // header does not flicker between states.
  const [measured, setMeasured] = useState(!logoUrl)

  useEffect(() => {
    if (!logoUrl) {
      setLayoutState(LAYOUT_STATE.TEXT)
      setMeasured(true)
      return undefined
    }

    let active = true
    setMeasured(false)
    setLayoutState(LAYOUT_STATE.TEXT)

    const image = new Image()

    image.onload = () => {
      if (!active) return
      setLayoutState(
        resolveLayoutState(image.naturalWidth, image.naturalHeight)
      )
      setMeasured(true)
    }

    image.onerror = () => {
      if (!active) return
      setLayoutState(LAYOUT_STATE.TEXT)
      setMeasured(true)
    }

    image.src = logoUrl

    return () => {
      active = false
    }
  }, [logoUrl])

  const renderContent = () => {
    if (measured && layoutState === LAYOUT_STATE.HORIZONTAL) {
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Box
            alt={location.displayName}
            component="img"
            data-testid="location-logo"
            src={logoUrl}
            sx={{
              maxHeight: '40px',
              maxWidth: '200px',
              objectFit: 'contain',
            }}
          />
          <AddressBlock align="center" location={location} />
        </Box>
      )
    }

    if (measured && layoutState === LAYOUT_STATE.COMPACT) {
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            justifyContent: 'center',
          }}
        >
          <Box
            alt={location.displayName}
            component="img"
            data-testid="location-logo"
            src={logoUrl}
            sx={{
              maxHeight: '80px',
              maxWidth: '60px',
              objectFit: 'contain',
            }}
          />
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <AddressBlock align="center" location={location} />
          </Box>
        </Box>
      )
    }

    // State A (no logo or failed load). While a logo is still being measured we
    // intentionally omit the display name to avoid flashing text before the
    // logo commits; the address keeps the reserved space populated.
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {measured && layoutState === LAYOUT_STATE.TEXT && (
          <Typography align="center" variant="h5">
            {location.displayName}
          </Typography>
        )}
        <AddressBlock align="center" location={location} />
      </Box>
    )
  }

  return (
    <Box
      data-testid="location-branding"
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        // Reserve a stable height so selecting B/C after the image loads does
        // not shift downstream page content (header height budget: max 8px
        // delta across states).
        minHeight: '76px',
        width: '100%',
      }}
    >
      {renderContent()}
    </Box>
  )
}

AddressBlock.propTypes = {
  align: PropTypes.string,
  location: PropTypes.object.isRequired,
}

LocationBranding.propTypes = {
  location: PropTypes.shape({
    bookingLogoUrl: PropTypes.string,
    city: PropTypes.string,
    displayName: PropTypes.string,
    phoneNumber: PropTypes.string,
    street: PropTypes.string,
  }).isRequired,
}
